import type React from 'react';
import { useTranslation } from 'react-i18next';
import { type SecondLevelNavItem, type SiteConfig } from '@cx-web/domain-types';

import { useMenuOverflow } from '../../hooks/use-menu-overflow';
import CustomDropdown from '../CustomDropdown/CustomDropdown';
import { HeaderNavItem } from '../HeaderNavItem/index';

import styles from './ExpandableSecondaryNav.module.css';

type ExpandableSecondaryNavProps = {
  bookmakers: SiteConfig['bookmakers'];
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  isUkStyle?: boolean;
  links: SecondLevelNavItem[];
  serverUrl: string;
};

const ExpandableSecondaryNav = ({
  bookmakers,
  dropdownRef,
  isUkStyle,
  links,
  serverUrl,
}: ExpandableSecondaryNavProps) => {
  const { t } = useTranslation(['label']);
  const { containerRef, dropdownItems, mainItems, measureRef, showMoreDropdown } = useMenuOverflow(links);

  return (
    <div className={styles.wrapper} data-testid="secondary-header-expandable-nav" ref={containerRef}>
      <ul className={styles.navItems}>
        {mainItems.map((link, index) => (
          <HeaderNavItem
            {...link}
            bookmakers={bookmakers}
            dropdownRef={dropdownRef}
            isUkStyle={isUkStyle}
            key={`${link.link}-${index}`}
            serverUrl={serverUrl}
            variant="secondary"
          />
        ))}
      </ul>

      {showMoreDropdown && (
        <CustomDropdown
          dropdownRef={dropdownRef}
          isUkStyle={isUkStyle}
          label={t('RESPONSIVE_MORE')}
          variant="secondary"
        >
          <div className={styles.moreContainer}>
            <ul className={styles.moreGrid} data-testid="secondary-header-more-grid">
              {dropdownItems.map((link, index) => (
                <HeaderNavItem
                  {...link}
                  bookmakers={bookmakers}
                  dropdownRef={dropdownRef}
                  isUkStyle={isUkStyle}
                  key={`${link.link}-${index}`}
                  serverUrl={serverUrl}
                  variant="secondary"
                />
              ))}
            </ul>
          </div>
        </CustomDropdown>
      )}

      {/* Hidden measurement copy of the full list + the More trigger — read for widths, never seen.
          visibility:hidden keeps it out of the tab order and the accessibility tree; dropdownRef is
          passed so expandable items measure at the same width as the visible row. */}
      <div className={styles.measureClip}>
        <ul aria-hidden className={styles.measure} data-testid="secondary-header-nav-measure" ref={measureRef}>
          {links.map((link, index) => (
            <HeaderNavItem
              {...link}
              bookmakers={bookmakers}
              dropdownRef={dropdownRef}
              isUkStyle={isUkStyle}
              key={`${link.link}-${index}`}
              serverUrl={serverUrl}
              variant="secondary"
            />
          ))}
          <li className={styles.measureMore}>{t('RESPONSIVE_MORE')}</li>
        </ul>
      </div>
    </div>
  );
};

export { ExpandableSecondaryNav };
